import { Card, CardContent } from '@/components/ui/card';
import { Flame } from 'lucide-react';
import type { GoalSummaryData } from '@/types/goals';
import { cn } from '@/lib/utils';
import {
  SLIDE_CARD,
  SLIDE_PADDING,
  SLIDE_SCROLL_BOTTOM_PAD,
  STORY_CARD_BG,
  NARRATIVE,
} from './constants';
import { StoryCardHeader } from './StoryCardHeader';
import { Counter } from './StoryCounter';
import { streakBarHeight } from './utils';

export function CardStreak({
  summary,
  active,
  className,
}: Readonly<{
  summary: GoalSummaryData;
  active: boolean;
  className?: string;
}>) {
  const n = Math.min(summary.max_streak, 30);
  const bars = Array.from({ length: n }, (_, i) => i);

  return (
    <Card
      className={cn(SLIDE_CARD, 'relative overflow-hidden', STORY_CARD_BG, className)}
    >
      <CardContent
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-y-contain sm:gap-6',
          SLIDE_PADDING,
          SLIDE_SCROLL_BOTTOM_PAD
        )}
      >
        <StoryCardHeader
          icon={<Flame className="h-5 w-5 text-orange-500" />}
          title="อ่านติดต่อกันมากสุด"
          subtitle="Streak สูงสุดในเส้นทางนี้"
          narrative={NARRATIVE.streak}
        />

        <div className="relative flex flex-col items-center justify-center">
          <div
            className="absolute -inset-6 rounded-full bg-orange-500/10 blur-2xl"
            aria-hidden
          />
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full border-2 border-orange-500/30 bg-gradient-to-br from-orange-500/20 to-orange-600/5 shadow-lg shadow-orange-500/10 sm:h-28 sm:w-28">
            <Flame className="h-12 w-12 text-orange-500 drop-shadow-sm sm:h-14 sm:w-14" />
          </div>
        </div>

        <div className="text-center">
          <p className="text-4xl font-bold tabular-nums text-orange-500 sm:text-6xl">
            <Counter
              value={summary.max_streak}
              active={active}
              duration={1200}
            />
          </p>
          <p className="mt-1 text-sm font-medium text-muted-foreground">วัน</p>
        </div>

        {summary.current_streak > 0 && (
          <div className="w-full max-w-xs rounded-2xl border border-orange-500/20 bg-background/50 px-6 py-4 text-center shadow-sm mix-blend-multiply dark:bg-card/50 dark:mix-blend-normal">
            <p className="text-xs font-medium uppercase tracking-wide text-orange-600 dark:text-orange-400">
              Streak ปัจจุบัน
            </p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-orange-500">
              <Counter
                value={summary.current_streak}
                active={active}
                duration={1000}
              />
              <span className="text-base font-normal text-muted-foreground">
                {' '}
                วัน
              </span>
            </p>
          </div>
        )}

        <div className="flex max-w-full flex-wrap items-end justify-center gap-1 overflow-x-auto pb-1 scrollbar-hide sm:flex-nowrap">
          {bars.map((i) => (
            <div
              key={i}
              className="w-2 rounded-t-full bg-gradient-to-t from-orange-600 to-orange-400 opacity-90 shadow-sm"
              style={{
                height: streakBarHeight(i, n),
                opacity: 0.35 + (i / Math.max(n - 1, 1)) * 0.65,
              }}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
